import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { NgbDateStruct, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { EMPTY, merge, of, Subject, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  pairwise,
  switchMap,
  tap,
  take
} from 'rxjs/operators';
import {
  Client,
  BookingSessionType,
  CurrencyBalance
} from 'src/app/core/models/trainer.models';
import {
  formatSessionCountForDisplay,
  getBalanceDisplayLinesForBooking,
  sessionCountForBookingSlot
} from 'src/app/core/utils/pt-client-balance-display';
import {
  OfferedSessionType,
  PublicTrainerRow,
  REPEAT_DAY_LABELS,
  offeredSessionTypeCode,
  repeatByDayToApiString,
  TrainingSessionBookingPreviewResponse
} from 'src/app/core/models/pt-booking.models';
import { AppSettings } from 'src/app/core/appsettings';
import {
  normalizeCurrencyBalanceRow,
  normalizePtSearchClientRecords
} from 'src/app/core/utils/member-pt-search-normalize';
import { TrainerService } from 'src/app/core/services/trainer.service';
import { ToastService } from 'src/app/core/services/toast-service';

@Component({
  selector: 'app-add-session',
  templateUrl: './add-session.component.html'
})
export class AddSessionComponent implements OnInit, OnChanges, OnDestroy {
  /** Matches df-frontdesk SelectTrainerMember._maxGroupSelection */
  private static readonly MAX_GROUP_MEMBERS = 4;
  private static readonly MIN_GROUP_MEMBERS = 2;
  /** df-frontdesk `maxRepeatInterval` / `maxOccurrences` (recurrence section). */
  private static readonly MAX_REPEAT_INTERVAL = 9;
  private static readonly MAX_OCCURRENCES = 999;

  @ViewChild('content') content!: ElementRef;
  @ViewChild('selectMembersContent') selectMembersContent!: ElementRef;
  @ViewChild('reviewBookingContent') reviewBookingContent!: ElementRef;

  @Input() action: string = 'Add';
  @Input() name: string | undefined;
  @Input() trainerId: number | undefined;
  @Input() scheduledEvent: any = null;
  @Input() allScheduled: Array<any> | undefined;
  @Output() submit = new EventEmitter<any>();
  @Output() cancelSession = new EventEmitter<any>();
  /** Emitted after Add flow succeeds via Preview → Confirm (refresh lists). */
  @Output() bookingSuccess = new EventEmitter<void>();

  /** Clients loaded from Member/PTSearch when user opens the dropdown (or when opening edit modal). */
  clientsList: Client[] = [];
  clientsLoading = false;

  /** Select members modal: search term and modal ref. */
  memberSearchTerm = '';
  selectMembersModalRef: any = null;

  /** Precomputed rows for Select members modal — avoids calling heavy getters every change detection. */
  membersForSelectModal: Array<any> = [];

  private readonly memberSearchInput$ = new Subject<string>();
  /** Loads without debounce (modal open / explicit refresh). Merged with debounced typing; `switchMap` cancels stale HTTP. */
  private readonly immediatePtSearch$ = new Subject<string>();
  private memberSearchSub: Subscription | null = null;

  /**
   * Avoids duplicate `Member/PTSearch` POSTs when the user re-opens the client dropdown or Select members modal
   * with an empty search while the roster is unchanged (same trainer, last response was already for `''`).
   */
  private static readonly MEMBER_ROSTER_CACHE_TTL_MS = 45_000;
  private lastMemberSearchTermResolved = '';
  private memberRosterEmptyFetchTrainerId: number | null = null;
  private memberRosterEmptyFetchAt = 0;

  customers!: Array<any>;

  endTime = '12:30 PM';
  selectedCustomer: number = -1;
  form!: FormGroup;
  clients: Array<any> = [];

  /** df-frontdesk `AddSessionRecurrenceSection` — inline card, not a second modal. */
  isRecurringShift = false;
  repeatIntervalStr = '1';
  repeatFrequency: 'Daily' | 'Weekly' = 'Weekly';
  repeatByDay: boolean[] = [false, false, false, false, false, false, false];
  recurrenceEndType: 'never' | 'after' | 'on' = 'after';
  recurrenceOccurrencesStr = '1';
  recurrenceEndDate: Date | null = null;

  /** df-frontdesk `_errorMessage` banner below header (recurrence validation, etc.). */
  addSessionErrorBanner: string | null = null;

  readonly repeatDayLabels = REPEAT_DAY_LABELS;

  /**
   * Narrow viewports: use `input type="date|time"` so OS pickers replace ngb-datepicker + Flatpickr
   * (avoids multi-tap calendar bugs and keyboard covering the time UI).
   */
  useNativeSessionPickers = false;
  private nativePickerMqlCleanup: (() => void) | null = null;
  private visualViewportCleanup: (() => void) | null = null;
  private visualViewportScrollRaf = 0;

  /** Add flow: trainers from Meta/TrainingTab/Public */
  trainersList: PublicTrainerRow[] = [];
  trainersLoading = false;
  selectedTrainerId: number | null = null;

  addModalRef: any = null;
  reviewModalRef: any = null;
  previewLoading = false;
  confirmLoading = false;
  reviewPreview: TrainingSessionBookingPreviewResponse | null = null;
  reviewExtraErrors: string[] = [];
  confirmApiErrors: string[] = [];
  lastPtBookingBody: Record<string, unknown> | null = null;

  /**
   * Portal for Flatpickr: inside the modal tree, not `document.body`. NgbModalStack sets `aria-hidden` on every
   * `body` child except the active `ngb-modal-window`; a body-appended calendar gets `aria-hidden` while the hour
   * input inside it still has focus (Chrome a11y warning). A dedicated small mount keeps `appendTo` minimal so
   * outside-click close still works (unlike `appendTo: document.body`).
   */
  /** `mwlFlatpickr.appendTo` is typed as `HTMLElement`; mount exists whenever this template is in the DOM (modal open). */
  get sessionTimeFlatpickrAppendTo(): HTMLElement {
    return document.querySelector('.su-root .su-fpm') as HTMLElement;
  }

  /**
   * Modal + scroll-lock can misplace Flatpickr’s absolute popup; pin with fixed + viewport coords (time picker only).
   */
  pinFlatpickrTimeInModal(evt: { instance?: { calendarContainer?: HTMLElement; _input: HTMLElement } }): void {
    const inst = evt?.instance as { calendarContainer?: HTMLElement; _input?: HTMLElement } | undefined;
    const cal = inst?.calendarContainer;
    const input = inst?._input;
    if (!cal || !input) {
      return;
    }
    const position = () => {
      const rect = input.getBoundingClientRect();
      const calH = cal.offsetHeight || 80;
      const calW = cal.offsetWidth || 280;
      const margin = 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      let top = rect.bottom + 2;
      if (calH + 2 > spaceBelow && spaceAbove >= spaceBelow) {
        top = rect.top - calH - 2;
      }
      top = Math.max(margin, Math.min(top, window.innerHeight - calH - margin));
      let left = rect.left;
      if (left + calW > window.innerWidth - margin) {
        left = window.innerWidth - calW - margin;
      }
      left = Math.max(margin, left);
      cal.style.position = 'fixed';
      cal.style.top = `${Math.round(top)}px`;
      cal.style.left = `${Math.round(left)}px`;
      cal.style.right = 'auto';
      cal.style.bottom = 'auto';
      cal.style.zIndex = '20000';
      cal.style.pointerEvents = 'auto';
    };
    requestAnimationFrame(() => {
      position();
      requestAnimationFrame(position);
    });
  }

  /** Flatpickr can update the control outside Angular’s usual path — resync validators for Preview. */
  onSessionTimeFlatpickrChange(_evt: unknown): void {
    this.ngZone.run(() => {
      this.form.get('sessiontime')?.updateValueAndValidity({ emitEvent: true });
      this.cdr.markForCheck();
    });
  }

  /** ngb-datepicker can leave `sessiondate` stale for `form.valid` vs displayed value. */
  onSessionDatePickerChange(): void {
    this.form.get('sessiondate')?.updateValueAndValidity({ emitEvent: true });
    this.cdr.markForCheck();
  }

  private wireNativePickerBreakpoint(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(max-width: 767.98px)');
    const apply = (): void => {
      this.ngZone.run(() => {
        const next = mq.matches;
        if (next === this.useNativeSessionPickers) {
          return;
        }
        this.useNativeSessionPickers = next;
        if (next) {
          this.attachVisualViewportScrollFix();
        } else {
          this.detachVisualViewportScrollFix();
        }
        this.cdr.markForCheck();
      });
    };
    apply();
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', apply);
      this.nativePickerMqlCleanup = () => mq.removeEventListener('change', apply);
    } else {
      mq.addListener(apply);
      this.nativePickerMqlCleanup = () => mq.removeListener(apply);
    }
  }

  private attachVisualViewportScrollFix(): void {
    this.detachVisualViewportScrollFix();
    const vv = window.visualViewport;
    if (!vv) {
      return;
    }
    const onVvChange = (): void => {
      if (this.visualViewportScrollRaf) {
        cancelAnimationFrame(this.visualViewportScrollRaf);
      }
      this.visualViewportScrollRaf = requestAnimationFrame(() => {
        this.visualViewportScrollRaf = 0;
        this.ngZone.run(() => this.scrollActiveNativeFieldIntoView());
      });
    };
    vv.addEventListener('resize', onVvChange);
    vv.addEventListener('scroll', onVvChange);
    this.visualViewportCleanup = () => {
      if (this.visualViewportScrollRaf) {
        cancelAnimationFrame(this.visualViewportScrollRaf);
        this.visualViewportScrollRaf = 0;
      }
      vv.removeEventListener('resize', onVvChange);
      vv.removeEventListener('scroll', onVvChange);
      this.visualViewportCleanup = null;
    };
  }

  private detachVisualViewportScrollFix(): void {
    this.visualViewportCleanup?.();
    this.visualViewportCleanup = null;
  }

  private scrollActiveNativeFieldIntoView(): void {
    if (!this.useNativeSessionPickers) {
      return;
    }
    const a = document.activeElement as HTMLElement | null;
    if (!a?.closest('.su-root')) {
      return;
    }
    this.scrollFieldIntoModalBody(a);
  }

  /** Keeps date/time fields visible when the on-screen keyboard opens (iOS / Android). */
  scrollFieldIntoModalBody(el: HTMLElement): void {
    const run = (): void => {
      el.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
    };
    requestAnimationFrame(run);
    setTimeout(run, 120);
    setTimeout(run, 380);
  }

  onNativeSessionFieldFocus(ev: FocusEvent): void {
    const el = ev.target as HTMLElement;
    this.scrollFieldIntoModalBody(el);
    if (this.useNativeSessionPickers && window.visualViewport) {
      setTimeout(() => this.scrollFieldIntoModalBody(el), 450);
    }
  }

  private ngbStructToIsoDate(s: NgbDateStruct): string {
    return moment({ year: s.year, month: s.month - 1, date: s.day }).format('YYYY-MM-DD');
  }

  sessionDateIsoMin(): string {
    return this.ngbStructToIsoDate(this.ngbDateMinBound());
  }

  sessionDateIsoMax(): string {
    return this.ngbStructToIsoDate(this.ngbDateMaxBound());
  }

  sessionDateIsoForNativeInput(): string {
    const v = this.form?.get('sessiondate')?.value;
    if (v == null || v === '') {
      return '';
    }
    const d = v instanceof Date ? v : new Date(typeof v === 'number' ? v : Date.parse(String(v)));
    if (Number.isNaN(d.getTime())) {
      return '';
    }
    return moment(d).format('YYYY-MM-DD');
  }

  onNativeSessionDateIsoChange(iso: string): void {
    const c = this.form.get('sessiondate');
    if (!c) {
      return;
    }
    const trimmed = (iso ?? '').trim();
    if (!trimmed) {
      c.setValue(null);
      this.onSessionDatePickerChange();
      return;
    }
    let day = moment(trimmed, 'YYYY-MM-DD', true);
    if (!day.isValid()) {
      return;
    }
    const min = moment(this.sessionDateIsoMin(), 'YYYY-MM-DD', true);
    const max = moment(this.sessionDateIsoMax(), 'YYYY-MM-DD', true);
    if (min.isValid() && day.isBefore(min)) {
      day = min;
    }
    if (max.isValid() && day.isAfter(max)) {
      day = max;
    }
    c.setValue(day.startOf('day').toDate());
    this.onSessionDatePickerChange();
    if (this.isRecurringShift) {
      this.applyRepeatByDayFromStartDate();
    }
  }

  sessionTimeHHmmForNativeInput(): string {
    const v = this.form?.get('sessiontime')?.value;
    if (v == null || v === '') {
      return '';
    }
    if (v instanceof Date) {
      return moment(v).format('HH:mm');
    }
    const m = moment(v, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss', 'HH:mm'], true);
    return m.isValid() ? m.format('HH:mm') : '';
  }

  onNativeSessionTimeHHmmChange(hhmm: string): void {
    const c = this.form.get('sessiontime');
    if (!c) {
      return;
    }
    const trimmed = (hhmm ?? '').trim();
    if (!trimmed) {
      c.setValue('');
      c.updateValueAndValidity({ emitEvent: true });
      this.cdr.markForCheck();
      return;
    }
    let m = moment(trimmed, 'HH:mm', true);
    if (!m.isValid()) {
      return;
    }
    const totalMin = m.hours() * 60 + m.minutes();
    const snapped = Math.round(totalMin / 15) * 15;
    const hour = Math.floor(snapped / 60) % 24;
    const minute = snapped % 60;
    m = moment().hour(hour).minute(minute).second(0).millisecond(0);
    c.setValue(m.format('h:mm A'));
    c.updateValueAndValidity({ emitEvent: true });
    this.cdr.markForCheck();
  }

  recurrenceEndDateIsoMin(): string {
    return this.ngbStructToIsoDate(this.ngbRecurrenceEndDateMinStruct());
  }

  recurrenceEndDateIsoForNativeInput(): string {
    if (this.recurrenceEndDate == null) {
      return '';
    }
    return moment(this.recurrenceEndDate).format('YYYY-MM-DD');
  }

  onNativeRecurrenceEndDateIsoChange(iso: string): void {
    const trimmed = (iso ?? '').trim();
    if (!trimmed) {
      this.recurrenceEndDate = null;
      this.cdr.markForCheck();
      return;
    }
    let day = moment(trimmed, 'YYYY-MM-DD', true);
    if (!day.isValid()) {
      return;
    }
    const min = moment(this.recurrenceEndDateIsoMin(), 'YYYY-MM-DD', true);
    const max = moment(this.sessionDateIsoMax(), 'YYYY-MM-DD', true);
    if (min.isValid() && day.isBefore(min)) {
      day = min;
    }
    if (max.isValid() && day.isAfter(max)) {
      day = max;
    }
    this.recurrenceEndDate = day.toDate();
    this.cdr.markForCheck();
  }

  /**
   * `container="body"` + ng-bootstrap’s appendToBody math is wrong under scroll; we pin with fixed + viewport rects.
   * `NgbInputDatepicker` reapplies `style` on every `NgZone.onStable` (e.g. after clicking a day), which would
   * snap the calendar back to the wrong place. A continuous rAF loop fixes that but breaks mobile hit-testing;
   * instead we re-pin only when the popup’s `style` attribute changes (MutationObserver + one rAF).
   */
  private ngbBodyDatepickerPinRaf = 0;
  private ngbBodyDatepickerPinFlushTimer = 0;
  private ngbBodyDatepickerStyleObserver: MutationObserver | null = null;
  private ngbBodyDatepickerObserverRaf = 0;

  toggleBodyDatepicker(dp: { isOpen(): boolean; toggle(): void }, anchor: HTMLElement | ElementRef<HTMLElement>): void {
    const el = anchor instanceof ElementRef ? anchor.nativeElement : anchor;
    const wasOpen = dp.isOpen();
    dp.toggle();
    if (wasOpen) {
      this.stopNgbBodyDatepickerPinLoop();
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.startNgbBodyDatepickerPinLoop(el);
    });
  }

  private startNgbBodyDatepickerPinLoop(anchor: HTMLElement): void {
    this.stopNgbBodyDatepickerPinLoop();
    let bootAttempts = 0;
    const boot = (): void => {
      bootAttempts++;
      const menu = this.findOpenSuNgbDatepickerMenu();
      if (!menu) {
        if (bootAttempts < 90) {
          this.ngbBodyDatepickerPinRaf = requestAnimationFrame(boot);
        } else {
          this.ngbBodyDatepickerPinRaf = 0;
        }
        return;
      }
      this.applyFixedPositionToNgbDatepickerMenu(menu, anchor);
      this.attachNgbBodyDatepickerStyleObserver(menu, anchor);
      this.ngbBodyDatepickerPinRaf = requestAnimationFrame(() => {
        const m = this.findOpenSuNgbDatepickerMenu();
        if (m?.classList.contains('show')) {
          this.applyFixedPositionToNgbDatepickerMenu(m, anchor);
        }
        this.ngbBodyDatepickerPinRaf = 0;
      });
    };
    this.ngbBodyDatepickerPinRaf = requestAnimationFrame(() => requestAnimationFrame(boot));

    this.ngbBodyDatepickerPinFlushTimer = window.setTimeout(() => {
      this.ngbBodyDatepickerPinFlushTimer = 0;
      const m = this.findOpenSuNgbDatepickerMenu();
      if (m?.classList.contains('show')) {
        this.applyFixedPositionToNgbDatepickerMenu(m, anchor);
      }
    }, 280);
  }

  private attachNgbBodyDatepickerStyleObserver(menu: HTMLElement, anchor: HTMLElement): void {
    this.detachNgbBodyDatepickerStyleObserver();
    const obs = new MutationObserver(() => {
      obs.disconnect();
      if (this.ngbBodyDatepickerObserverRaf) {
        cancelAnimationFrame(this.ngbBodyDatepickerObserverRaf);
      }
      this.ngbBodyDatepickerObserverRaf = requestAnimationFrame(() => {
        this.ngbBodyDatepickerObserverRaf = 0;
        if (!menu.isConnected || !menu.classList.contains('show')) {
          this.detachNgbBodyDatepickerStyleObserver();
          return;
        }
        this.applyFixedPositionToNgbDatepickerMenu(menu, anchor);
        obs.observe(menu, { attributes: true, attributeFilter: ['style', 'class'] });
      });
    });
    obs.observe(menu, { attributes: true, attributeFilter: ['style', 'class'] });
    this.ngbBodyDatepickerStyleObserver = obs;
  }

  private detachNgbBodyDatepickerStyleObserver(): void {
    if (this.ngbBodyDatepickerObserverRaf) {
      cancelAnimationFrame(this.ngbBodyDatepickerObserverRaf);
      this.ngbBodyDatepickerObserverRaf = 0;
    }
    this.ngbBodyDatepickerStyleObserver?.disconnect();
    this.ngbBodyDatepickerStyleObserver = null;
  }

  private stopNgbBodyDatepickerPinLoop(): void {
    if (this.ngbBodyDatepickerPinRaf) {
      cancelAnimationFrame(this.ngbBodyDatepickerPinRaf);
      this.ngbBodyDatepickerPinRaf = 0;
    }
    if (this.ngbBodyDatepickerPinFlushTimer) {
      clearTimeout(this.ngbBodyDatepickerPinFlushTimer);
      this.ngbBodyDatepickerPinFlushTimer = 0;
    }
    this.detachNgbBodyDatepickerStyleObserver();
  }

  private findOpenSuNgbDatepickerMenu(): HTMLElement | null {
    const menus = document.querySelectorAll('body > .dropdown-menu.su-ngdp');
    for (let i = menus.length - 1; i >= 0; i--) {
      const el = menus[i] as HTMLElement;
      if (el.classList.contains('show')) {
        return el;
      }
    }
    return null;
  }

  private applyFixedPositionToNgbDatepickerMenu(menu: HTMLElement, anchor: HTMLElement): void {
    const rect = anchor.getBoundingClientRect();
    const margin = 8;
    const h = menu.offsetHeight || 300;
    const w = menu.offsetWidth || 280;
    let top = rect.bottom + 2;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    if (h + 2 > spaceBelow && spaceAbove > spaceBelow) {
      top = rect.top - h - 2;
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - h - margin));
    let left = rect.left;
    if (left + w > window.innerWidth - margin) {
      left = window.innerWidth - w - margin;
    }
    left = Math.max(margin, left);
    menu.style.position = 'fixed';
    menu.style.top = `${Math.round(top)}px`;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
    menu.style.transform = 'none';
    menu.style.zIndex = '20050';
    menu.style.pointerEvents = 'auto';
  }

  private formUiSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private trainerService: TrainerService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.loadOfferedSessionTypes();
    this.form = this.fb.group({
      sessiontype: [{ value: null, disabled: this.isEditing() }],
      sessiondate: [{ value: null, disabled: this.isEditing() }, Validators.required],
      sessiontime: [{ value: '', disabled: this.isEditing() }, Validators.required],
    });
    this.form.get('sessiontype')?.valueChanges
      .pipe(pairwise())
      .subscribe(([prev, next]: [any, any]) => {
        const wasGroup = this.sessionTypeOption(prev)?.isGroup ?? false;
        const nowGroup = this.sessionTypeOption(next)?.isGroup ?? false;
        if (!wasGroup && nowGroup) {
          // Private → Group: keep selection; user can add up to 4 (df-frontdesk).
        } else if (wasGroup && !nowGroup) {
          // Group → Private: keep only first.
          if (this.clients.length > 1) {
            this.clients = [this.clients[0]];
          }
        } else if (!nowGroup && this.clients.length > 1) {
          // Private → other private duration: single member only (df-frontdesk).
          this.clients = [this.clients[0]];
        }
        this.enforceBalanceForSessionType();
        if (this.clientsList?.length) {
          this.customers = this.getCustomers();
          this.rebuildMembersForSelectModal();
        }
        this.cdr.markForCheck();
      });
    this.customers = this.getCustomers();
    this.rebuildMembersForSelectModal();

    this.formUiSub = merge(this.form.valueChanges, this.form.statusChanges).subscribe(() =>
      this.cdr.markForCheck()
    );

    const debouncedSearch = this.memberSearchInput$.pipe(debounceTime(350), distinctUntilChanged());
    this.memberSearchSub = merge(this.immediatePtSearch$, debouncedSearch)
      .pipe(
        switchMap(term => {
          const tid = this.resolvePtSearchTrainerId();
          if (tid === null) {
            return EMPTY;
          }
          this.clientsLoading = true;
          this.cdr.markForCheck();
          return this.trainerService.searchPtClients$(tid, term).pipe(
            tap((list: any[]) => {
              this.clientsList = normalizePtSearchClientRecords(list);
              this.customers = this.getCustomers();
              this.refreshSelectedClientsDisplay();
              this.lastMemberSearchTermResolved = term;
              if (term === '') {
                this.memberRosterEmptyFetchTrainerId = tid;
                this.memberRosterEmptyFetchAt = Date.now();
              }
            }),
            catchError(() => {
              this.clientsList = [];
              this.customers = [];
              this.lastMemberSearchTermResolved = '';
              this.memberRosterEmptyFetchTrainerId = null;
              this.memberRosterEmptyFetchAt = 0;
              this.rebuildMembersForSelectModal();
              this.cdr.markForCheck();
              return of([] as any[]);
            }),
            finalize(() => {
              this.clientsLoading = false;
              this.cdr.markForCheck();
            })
          );
        })
      )
      .subscribe();

    this.wireNativePickerBreakpoint();
  }

  ngOnDestroy(): void {
    this.nativePickerMqlCleanup?.();
    this.nativePickerMqlCleanup = null;
    this.detachVisualViewportScrollFix();
    this.stopNgbBodyDatepickerPinLoop();
    this.memberSearchSub?.unsubscribe();
    this.memberSearchSub = null;
    this.formUiSub?.unsubscribe();
    this.formUiSub = undefined;
  }

  /** Same avatar resolution as `app-list-sessions` memberAvatar (feed `Member` shape). */
  private avatarUrlFromFeedMemberShape(m: any): string {
    if (m == null || typeof m !== 'object') {
      return '';
    }
    return (
      m.Avatar?.UploadedUrl
      ?? m.avatar?.uploadedUrl
      ?? m.UploadedUrl
      ?? m.uploadedUrl
      ?? ''
    );
  }

  private applyFeedAvatarToClient(client: Client, feedMember: any): void {
    const url = this.avatarUrlFromFeedMemberShape(feedMember);
    if (!url) {
      return;
    }
    client.Avatar = { ...(client.Avatar && typeof client.Avatar === 'object' ? client.Avatar : {}), UploadedUrl: url };
  }

  /** Find `Member` from the sessions feed (`scheduledEvent` / `allScheduled`) for avatar parity with the grid. */
  private findFeedMemberSnapshot(memberId: number): any | null {
    if (!Number.isFinite(memberId) || memberId <= 0) {
      return null;
    }
    const rows: any[] = [];
    if (this.scheduledEvent) {
      rows.push(this.scheduledEvent);
    }
    if (Array.isArray(this.allScheduled)) {
      rows.push(...this.allScheduled);
    }
    for (const s of rows) {
      const m = s?.Member ?? s?.member;
      const mid = Number(m?.Id ?? s?.MemberId ?? s?.memberId ?? 0);
      if (mid === memberId && m) {
        return m;
      }
    }
    return null;
  }

  /**
   * Build a `Client` from `GroupSession.Members[]` row (balances from unified
   * `GET Training/Booking/{id}`) when roster lookup misses the member.
   */
  private clientFromGroupSessionMemberRow(
    r: Record<string, unknown>,
    scheduleFallback: number
  ): Client {
    const mid = Number(r['MemberId'] ?? r['memberId'] ?? 0);
    const fn = String(r['MemberFirstName'] ?? r['memberFirstName'] ?? '').trim();
    const ln = String(r['MemberLastName'] ?? r['memberLastName'] ?? '').trim();
    const pt = Number(r['PTSessions'] ?? r['ptSessions']);
    const gs = Number(r['GroupSessions'] ?? r['groupSessions']);
    const balRaw = r['CurrencyBalances'] ?? r['currencyBalances'];
    let CurrencyBalances: CurrencyBalance[] | undefined;
    if (Array.isArray(balRaw) && balRaw.length) {
      const normalized = balRaw
        .map(entry =>
          entry != null && typeof entry === 'object'
            ? normalizeCurrencyBalanceRow(entry as Record<string, unknown>)
            : null
        )
        .filter((x): x is CurrencyBalance => x != null);
      if (normalized.length) {
        CurrencyBalances = normalized;
      }
    }
    const full =
      String(r['MemberFullName'] ?? r['memberFullName'] ?? '').trim() ||
      [fn, ln].filter(Boolean).join(' ') ||
      `Member ${mid}`;
    const rowAvatar =
      String(r['UploadedUrl'] ?? r['uploadedUrl'] ?? '').trim()
      || this.avatarUrlFromFeedMemberShape(r['Member'] ?? r['member'])
      || this.avatarUrlFromFeedMemberShape(r['Avatar'] ?? r['avatar']);
    const c: Client = {
      Id: mid,
      PersonId: 0,
      ScheduleId: scheduleFallback,
      Person: { FullName: full, FirstName: fn || undefined, LastName: ln || undefined },
      Avatar: rowAvatar ? { UploadedUrl: rowAvatar } : undefined,
      GroupSessions: Number.isFinite(gs) ? gs : 0,
      PTSessions: Number.isFinite(pt) ? pt : 0,
      State: 1
    };
    if (CurrencyBalances?.length) {
      c.CurrencyBalances = CurrencyBalances;
    }
    return c;
  }

  /** Preview/confirm body: positive schedule id from API, or `0` when Member/PTSearch omits it. */
  private coerceScheduleIdForPtBooking(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 0;
  }

  /** Load clients from Member/PTSearch. Call when dropdown opens or when opening edit modal. */
  loadClients(): void {
    this.fetchClientsForModal('');
  }

  /**
   * Debounced search in Select members modal (matches df-frontdesk SelectTrainerMember).
   */
  onMemberSearchInput(value: string): void {
    this.memberSearchTerm = value;
    this.memberSearchInput$.next((value ?? '').trim());
  }

  /** True when Add flow has a trainer selected (or Edit uses logged-in trainer). */
  hasTrainerForRoster(): boolean {
    return this.resolvePtSearchTrainerId() !== null;
  }

  private resolvePtSearchTrainerId(): number | null {
    if (this.scheduledEvent) {
      if (this.trainerId != null && this.trainerId !== undefined && this.trainerId > 0) {
        return this.trainerId;
      }
      return null;
    }
    const tid = this.selectedTrainerId ?? this.trainerId;
    if (tid != null && tid !== undefined && tid > 0) {
      return tid;
    }
    return null;
  }

  /** Display name for selected trainer (Add) or logged-in trainer fallback. */
  getTrainerSectionName(): string {
    if (this.scheduledEvent) {
      return this.name || '—';
    }
    const t = this.trainersList.find(tr => tr.Id === this.selectedTrainerId);
    if (t?.Person) {
      const p = t.Person;
      if (p.FullName) {
        return p.FullName;
      }
      return [p.FirstName, p.LastName].filter(Boolean).join(' ').trim() || '—';
    }
    return this.name || '—';
  }

  getSessionTypeSummary(): string {
    return `${this.selectedSessionInfo().label ?? 'Session'} · ${this.getTrainerSectionName()}`;
  }

  /** Review modal badges — "1 session" / "N sessions" (matches design, not "session(s)"). */
  get reviewSessionBadgeLabel(): string {
    const n = this.reviewPreview?.occurrenceCount ?? 0;
    return n === 1 ? '1 session' : `${n} sessions`;
  }

  get reviewMemberBadgeLabel(): string {
    const n = this.clients?.length ?? 0;
    return n === 1 ? '1 member' : `${n} members`;
  }

  /** Flutter: `Private/Group session with ${_selectedTrainer!.person.firstName}`. */
  getTrainerFirstNameForSubtitle(): string {
    if (this.scheduledEvent) {
      const se = this.scheduledEvent;
      const fn = se.Trainer?.Person?.FirstName ?? se.trainer?.person?.firstName
        ?? se.Member?.FirstName;
      if (fn) {
        return String(fn);
      }
      return (this.name || '—').split(/\s+/)[0];
    }
    const t = this.trainersList.find(tr => tr.Id === this.selectedTrainerId);
    const fn = t?.Person?.FirstName;
    if (fn) {
      return String(fn);
    }
    return (this.name || '—').split(/\s+/)[0];
  }

  get editDateBounds(): { minDate: Date; maxDate: Date } {
    const baseMin = moment().subtract(14, 'days').startOf('day');
    const baseMax = moment().add(60, 'days').startOf('day');
    const se = this.scheduledEvent;
    if (se?.StartDate) {
      const orig = moment(se.StartDate).startOf('day');
      return {
        minDate: moment.min(baseMin, orig).toDate(),
        maxDate: moment.max(baseMax, orig).toDate()
      };
    }
    return {
      minDate: baseMin.toDate(),
      maxDate: baseMax.toDate()
    };
  }

  /**
   * Datepicker should allow selecting up to 14 days in the past.
   */
  private sessionStartDateMinAsDate(): Date {
    return moment(this.editDateBounds.minDate).startOf('day').toDate();
  }

  ngbDateMinBound(): NgbDateStruct {
    const d = this.sessionStartDateMinAsDate();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  ngbDateMaxBound(): NgbDateStruct {
    const d = this.editDateBounds.maxDate;
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  ngbRecurrenceEndDateMinStruct(): NgbDateStruct {
    const d = this.getRecurrenceEndDateMin();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }

  onTrainerSelected(): void {
    this.clientsList = [];
    this.clients = [];
    this.customers = [];
    this.membersForSelectModal = [];
    this.lastMemberSearchTermResolved = '';
    this.memberRosterEmptyFetchTrainerId = null;
    this.memberRosterEmptyFetchAt = 0;
    this.addSessionErrorBanner = null;
    this.resetRecurrence();
    this.cdr.markForCheck();
  }

  dismissAddSessionError(): void {
    this.addSessionErrorBanner = null;
  }

  toggleRecurringShift(): void {
    this.isRecurringShift = !this.isRecurringShift;
    if (this.isRecurringShift) {
      this.applyRepeatByDayFromStartDate();
    }
  }

  onRepeatFrequencyChange(v: string): void {
    if (v !== 'Weekly') {
      this.repeatByDay = [false, false, false, false, false, false, false];
    }
  }

  toggleRepeatDay(index: number): void {
    const next = this.repeatByDay.map((b, i) => (i === index ? !b : b));
    if (!next.some(b => b)) {
      next[index] = true;
    }
    this.repeatByDay = next;
  }

  applyRepeatByDayFromStartDate(): void {
    const dateVal = this.form.get('sessiondate')?.value;
    if (dateVal == null || dateVal === '') {
      return;
    }
    const d =
      dateVal instanceof Date
        ? dateVal
        : new Date(typeof dateVal === 'number' ? dateVal : Date.parse(String(dateVal)));
    if (Number.isNaN(d.getTime())) {
      return;
    }
    const idx = d.getDay();
    const fresh = [false, false, false, false, false, false, false];
    fresh[idx] = true;
    this.repeatByDay = fresh;
  }

  getRecurrenceSummaryText(): string {
    if (!this.isRecurringShift || !this.hasSessionDate()) {
      return '';
    }
    const n = this.parsedRepeatInterval();
    const freq = this.repeatFrequency === 'Daily' ? 'day' : 'week';
    const every = n === 1 ? `every ${freq}` : `every ${n} ${freq}s`;
    if (this.repeatFrequency === 'Weekly') {
      const days: string[] = [];
      for (let i = 0; i < 7; i++) {
        if (this.repeatByDay[i]) {
          days.push(this.repeatDayLabels[i]);
        }
      }
      const dayStr = days.length ? ` on ${days.join(', ')}` : '';
      const count = this.recurrenceEndType === 'after' ? this.parsedRecurrenceOccurrences() : null;
      const times = count != null ? `, ${count} times` : '';
      return `Repeats ${every}${dayStr}${times}`;
    }
    const count = this.recurrenceEndType === 'after' ? this.parsedRecurrenceOccurrences() : null;
    const times = count != null ? `, ${count} times` : '';
    return `Repeats ${every}${times}`;
  }

  parsedRepeatInterval(): number {
    const v = parseInt(String(this.repeatIntervalStr).replace(/\D/g, ''), 10);
    return v >= 1 ? v : 1;
  }

  parsedRecurrenceOccurrences(): number {
    const v = parseInt(String(this.recurrenceOccurrencesStr).replace(/\D/g, ''), 10);
    return v >= 1 ? v : 1;
  }

  getRecurrenceEndDateMin(): Date {
    const dateVal = this.form.get('sessiondate')?.value;
    if (dateVal != null && dateVal !== '') {
      return moment(dateVal).startOf('day').toDate();
    }
    return this.sessionStartDateMinAsDate();
  }

  trainerOptionLabel(tr: PublicTrainerRow): string {
    const p = tr.Person;
    if (!p) {
      return `Trainer ${tr.Id}`;
    }
    if (p.FullName) {
      return p.FullName;
    }
    return [p.FirstName, p.LastName].filter(Boolean).join(' ').trim() || `Trainer ${tr.Id}`;
  }

  /** Add flow: only the logged-in trainer (`trainerId` / `name` inputs) — no public trainer list. */
  private setLoggedInTrainerOnly(): void {
    this.trainersLoading = false;
    const tid = this.trainerId != null && this.trainerId > 0 ? this.trainerId : null;
    this.selectedTrainerId = tid;
    if (tid == null) {
      this.trainersList = [];
      this.cdr.markForCheck();
      return;
    }
    const display = (this.name ?? '').trim();
    const first = display.split(/\s+/)[0] || '';
    const row: PublicTrainerRow = {
      Id: tid,
      State: 1,
      Person: {
        FullName: display || `Trainer ${tid}`,
        FirstName: first || undefined
      }
    };
    this.trainersList = [row];
    this.cdr.markForCheck();
  }

  private loadClientRowForEdit(): void {
    if (!this.scheduledEvent || !this.trainerId) {
      return;
    }
    const se = this.scheduledEvent;
    if (se.Type === BookingSessionType.GroupPT) {
      this.loadGroupSessionRowsForEdit();
      return;
    }
    const bookingId = se.Id ?? (se as { id?: number }).id;
    if (bookingId == null || bookingId <= 0) {
      return;
    }
    const memberId = se.Member?.Id ?? se.MemberId;
    const scheduleId = se.ScheduleId;
    this.clientsLoading = true;
    this.trainerService.getGroupSessionDetail(bookingId).then((detail: Record<string, unknown>) => {
      const rawMembers = (detail?.['Members'] ?? detail?.['members']) as unknown[] | undefined;
      const members: any[] = Array.isArray(rawMembers) ? rawMembers : [];
      const scheduleFallback = scheduleId ?? 0;

      const mid = Number(memberId ?? 0);
      const row = members.find((r) => Number((r as any)?.MemberId ?? (r as any)?.memberId ?? 0) === mid)
        ?? members[0];

      if (row) {
        const synthetic = this.clientFromGroupSessionMemberRow(row as Record<string, unknown>, Number(scheduleFallback));
        this.applyFeedAvatarToClient(synthetic, se.Member);
        this.clientsList = [synthetic];
        this.clients = [this.mapClientForSelection(synthetic, 'Member')];
      } else {
        this.clientsList = [];
        const m = se.Member || {};
        this.clients = [{
          id: memberId,
          scheduleId: scheduleFallback,
          avatar: m.UploadedUrl || m.Avatar?.UploadedUrl || '',
          name: m.FullName || [m.FirstName, m.LastName].filter(Boolean).join(' ') || 'Member',
          sessionsLeft: '—',
          disabled: false,
          state: 1,
          balanceLines: ['Balance unavailable — refresh roster after save.'],
          isInactive: false
        }];
      }
      this.customers = this.getCustomers();
    }).catch(() => {
      this.clientsList = [];
    }).finally(() => {
      this.clientsLoading = false;
      this.cdr.markForCheck();
    });
  }

  /** Group PT edit: load all members from `GET Training/Booking/{id}` → `GroupSession`. */
  private loadGroupSessionRowsForEdit(): void {
    const se = this.scheduledEvent;
    const bookingId = se.Id ?? (se as { id?: number }).id;
    if (bookingId == null || bookingId <= 0 || !this.trainerId) {
      return;
    }
    this.clientsLoading = true;
    // Single detail call: Training/Sessions/{bookingId} already returns all attendees for that group slot.
    this.trainerService.getGroupSessionDetail(bookingId).then((detail: Record<string, unknown>) => {
      const rowsByMemberId = new Map<number, Record<string, unknown>>();
      const rawMembers = (detail?.['Members'] ?? detail?.['members']) as unknown[] | undefined;
      const detailMembers: any[] = Array.isArray(rawMembers) ? rawMembers : [];
      for (const row of detailMembers) {
        const r = row as Record<string, unknown>;
        const mid = Number(r['MemberId'] ?? r['memberId'] ?? 0);
        if (!Number.isFinite(mid) || mid <= 0 || rowsByMemberId.has(mid)) {
          continue;
        }
        rowsByMemberId.set(mid, r);
      }

      // Fallback: if detail payload is sparse, still show all attendees from feed rows.
      if (!rowsByMemberId.size && Array.isArray(this.allScheduled)) {
        for (const s of this.allScheduled) {
          const m = s?.Member ?? s?.member;
          const mid = Number(m?.Id ?? s?.MemberId ?? s?.memberId ?? 0);
          if (!Number.isFinite(mid) || mid <= 0 || rowsByMemberId.has(mid)) {
            continue;
          }
          rowsByMemberId.set(mid, {
            MemberId: mid,
            FullName: m?.Person?.FullName ?? m?.FullName ?? `${m?.FirstName ?? ''} ${m?.LastName ?? ''}`.trim(),
            UploadedUrl: m?.Avatar?.UploadedUrl ?? m?.UploadedUrl ?? '',
            State: m?.State ?? 1,
            CurrencyBalances: []
          });
        }
      }

      const mergedMembers = Array.from(rowsByMemberId.values());
      if (!mergedMembers.length) {
        this.clientsLoading = false;
        this.cdr.markForCheck();
        return;
      }

      const scheduleFallback = se.ScheduleId ?? (se as { scheduleId?: number }).scheduleId ?? 0;
      const mapped: Array<Record<string, unknown>> = [];
      const syntheticClients: Client[] = [];
      for (const row of mergedMembers) {
        const r = row as Record<string, unknown>;
        const mid = Number(r['MemberId'] ?? r['memberId'] ?? 0);
        const sid = Number(scheduleFallback);
        const synthetic = this.clientFromGroupSessionMemberRow(r, sid);
        this.applyFeedAvatarToClient(synthetic, this.findFeedMemberSnapshot(mid));
        syntheticClients.push(synthetic);
        mapped.push(this.mapClientForSelection(synthetic, `Member ${mid}`));
      }
      this.clientsList = syntheticClients;
      this.clients = mapped;
      this.customers = this.getCustomers();
      this.clientsLoading = false;
      this.cdr.markForCheck();
    }).catch(() => {
      this.clientsLoading = false;
      this.cdr.markForCheck();
    });
  }

  /** Triggers Member/PTSearch (immediate — no debounce). Superseded requests are cancelled via `switchMap`. */
  private fetchClientsForModal(searchTerm: string): void {
    const tid = this.resolvePtSearchTrainerId();
    if (tid === null) {
      return;
    }
    const term = (searchTerm ?? '').trim();
    if (
      term === '' &&
      this.lastMemberSearchTermResolved === '' &&
      this.clientsList.length > 0 &&
      this.memberRosterEmptyFetchTrainerId === tid &&
      Date.now() - this.memberRosterEmptyFetchAt < AddSessionComponent.MEMBER_ROSTER_CACHE_TTL_MS
    ) {
      return;
    }
    this.immediatePtSearch$.next(term);
  }

  /** Rebuilds `membersForSelectModal` from `clientsList` + `clients` (call after roster or selection changes). */
  private rebuildMembersForSelectModal(): void {
    const base = this.getCustomers();
    if (!this.clients?.length) {
      this.membersForSelectModal = base;
      return;
    }
    const inBaseIds = new Set(base.map(m => m.id));
    const trailing = this.clients
      .filter(c => !inBaseIds.has(c.id))
      .map(selected => ({
        ...selected,
        disabled: false,
        isInactive: selected.state !== 1
      }));
    this.membersForSelectModal = trailing.length ? [...base, ...trailing] : base;
  }

  /** Called when user opens the Client(s) dropdown; loads clients via Member/PTSearch if not yet loaded. */
  onClientsDropdownOpen(): void {
    this.loadClients();
  }

  /** When session type changes, remove selected clients with insufficient balance. */
  enforceBalanceForSessionType(): void {
    if (!this.clientsList?.length) {
      this.resetRecurrence();
      this.rebuildMembersForSelectModal();
      this.cdr.markForCheck();
      return;
    }
    if (this.clients.length) {
      this.clients = this.clients.filter(mapped => {
        const raw = this.clientsList.find((c: Client) => c.Id === mapped.id);
        return !!(raw && this.hasEnoughBalanceForSelection(raw));
      });
      this.refreshSelectedClientsDisplay();
    } else {
      this.rebuildMembersForSelectModal();
    }
    this.resetRecurrence();
    this.cdr.markForCheck();
  }

  /**
   * Rebuild mapped `clients` from `clientsList` so balance lines and session counts match the
   * current session type (df-frontdesk updates display on type change).
   */
  private refreshSelectedClientsDisplay(): void {
    if (!this.clients.length || !this.clientsList?.length) {
      this.rebuildMembersForSelectModal();
      return;
    }
    this.clients = this.clients.map(m => {
      const raw = this.clientsList.find((c: Client) => c.Id === m.id);
      if (!raw) {
        return m;
      }
      return {
        ...this.mapClientForSelection(raw),
        name: raw.Person?.FullName || m.name || ''
      };
    });
    this.rebuildMembersForSelectModal();
  }

  private blurActiveElement(): void {
    (document.activeElement as HTMLElement | null)?.blur();
  }

  /**
   * Stacked modals: `NgbModal.open()` runs `NgbModalStack._setAriaHidden` synchronously, so the
   * underlying window gets `aria-hidden` before this call stack returns. Blur **before** `open()`
   * so nothing inside the parent dialog (e.g. `.su-fab`) stays focused there; then move
   * focus into the top dialog after its `shown` animation.
   */
  private moveFocusIntoStackedModal(modalRef: NgbModalRef, windowClass: string): void {
    modalRef.shown.pipe(take(1)).subscribe(() => {
      const nodes = document.querySelectorAll(`ngb-modal-window.${windowClass}.show`);
      const host = nodes.length ? (nodes[nodes.length - 1] as HTMLElement) : null;
      if (!host) {
        return;
      }
      if (windowClass === 'su-sw') {
        const search = host.querySelector<HTMLElement>('#select-members-search-input');
        if (search) {
          search.focus();
          return;
        }
      }
      const sel =
        'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      host.querySelector<HTMLElement>(sel)?.focus();
    });
  }

  /** Open the Select members modal (loads clients on first open). */
  openSelectMembersModal(): void {
    this.memberSearchTerm = '';
    this.fetchClientsForModal('');
    this.blurActiveElement();
    this.selectMembersModalRef = this.modalService.open(this.selectMembersContent, {
      size: 'sm',
      modalDialogClass: 'su-smd',
      backdrop: true,
      backdropClass: 'su-sbd',
      windowClass: 'su-sw',
      centered: true
    });
    this.moveFocusIntoStackedModal(this.selectMembersModalRef, 'su-sw');
  }

  closeSelectMembersModal(): void {
    if (this.selectMembersModalRef) {
      this.selectMembersModalRef.close();
      this.selectMembersModalRef = null;
    }
    this.memberSearchTerm = '';
  }

  /** TrackBy for member list in Select members modal. */
  memberTrackBy(_index: number, member: any): number {
    return member?.id ?? _index;
  }

  /** Subtitle for Select members modal: session type label. */
  getSelectMembersSubtitle(): string {
    const label = this.selectedSessionInfo().label ??
      `${this.isGroupSession() ? 'Group' : 'Private'} (${this.getDurationForSelection()} min)`;
    return `Choose ${this.isGroupSession() ? 'members' : 'a member'} for this "${label}" session`;
  }

  tap(elem: HTMLInputElement) {
    setTimeout(() => {
      elem.focus();
    }, 100);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.scheduledEvent && changes['trainerId'] && !changes['trainerId'].firstChange) {
      this.onTrainerSelected();
    }
    if (!this.scheduledEvent && (changes['trainerId'] || changes['name'])) {
      this.setLoggedInTrainerOnly();
    }
    this.customers = this.getCustomers();
    this.rebuildMembersForSelectModal();
    this.initScheduledEvent();
  }

  onSubmit(modal: any) {
    if (!this.scheduledEvent) {
      return;
    }
    modal.close('Submit');
    const startTime = `${moment(this.form.controls['sessiontime'].value, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss']).format('HH:mm')}:00`;
    this.submit.emit({
      session: {
        id: this.scheduledEvent.Id,
        scheduleId: this.scheduledEvent.ScheduleId,
        sessionType: this.isGroupSession() ? BookingSessionType.GroupPT : BookingSessionType.IndividualPT,
        duration: this.getDurationForSelection(),
        sessionDate: this.form.get('sessiondate')?.value,
        sessionTime: startTime,
        clients: this.clients,
      },
      recurrentDates: []
    });
  }

  onPreviewClick(_modal: any): void {
    if (this.scheduledEvent) {
      return;
    }
    this.addSessionErrorBanner = null;
    const recErr = this.validateRecurrenceForPreview();
    if (recErr) {
      this.addSessionErrorBanner = recErr;
      return;
    }
    const body = this.buildPtBookingRequest();
    if (!body) {
      this.toastService.showWarning('Complete the form before preview.');
      return;
    }
    this.lastPtBookingBody = body;
    this.previewLoading = true;
    this.reviewExtraErrors = [];
    this.confirmApiErrors = [];
    this.trainerService.previewPtBooking(body).then(result => {
      this.previewLoading = false;
      if (!result.httpOk) {
        const extra = result.transportError ? [result.transportError] : [];
        this.reviewExtraErrors = extra;
      }
      if (result.preview) {
        this.reviewPreview = result.preview;
      } else if (result.httpOk) {
        this.reviewExtraErrors = [...this.reviewExtraErrors, 'Could not read preview response.'];
        this.reviewPreview = {
          isValid: false,
          errors: [],
          occurrenceCount: 0,
          occurrences: [],
          attendees: []
        };
      } else {
        this.reviewPreview = result.preview ?? {
          isValid: false,
          errors: [],
          occurrenceCount: 0,
          occurrences: [],
          attendees: []
        };
      }
      this.openReviewModal();
    }).catch(() => {
      this.previewLoading = false;
      this.toastService.showDanger('Preview request failed.');
    });
  }

  /** Mirrors df-frontdesk `PtSessionRecurrenceValidate.validate`. */
  private validateRecurrenceForPreview(): string | null {
    if (!this.isRecurringShift) {
      return null;
    }
    const repeatInterval = this.parsedRepeatInterval();
    const repeatFrequency = this.repeatFrequency;
    const repeatByDay = this.repeatByDay;
    const recurrenceEndType = this.recurrenceEndType;
    const recurrenceOccurrencesCount = this.parsedRecurrenceOccurrences();
    const recurrenceEndDate = this.recurrenceEndDate;
    const sessionStartDate = this.form.get('sessiondate')?.value;

    if (repeatInterval < 1) {
      return 'Every must be at least 1.';
    }
    if (repeatInterval > AddSessionComponent.MAX_REPEAT_INTERVAL) {
      return `Every must be at most ${AddSessionComponent.MAX_REPEAT_INTERVAL}.`;
    }
    if (repeatFrequency === 'Weekly' && !repeatByDay.some(b => b)) {
      return 'Select at least one day under Repeat on.';
    }
    if (recurrenceEndType === 'after') {
      if (recurrenceOccurrencesCount < 1) {
        return 'Occurrences must be at least 1.';
      }
      if (recurrenceOccurrencesCount > AddSessionComponent.MAX_OCCURRENCES) {
        return `Occurrences must be at most ${AddSessionComponent.MAX_OCCURRENCES}.`;
      }
    }
    if (recurrenceEndType === 'on') {
      if (recurrenceEndDate == null) {
        return 'Select an end date.';
      }
      if (sessionStartDate != null && sessionStartDate !== '') {
        if (moment(recurrenceEndDate).startOf('day').isBefore(moment(sessionStartDate).startOf('day'))) {
          return 'End date must be on or after start date.';
        }
      }
    }
    return null;
  }

  private buildPtBookingRequest(): Record<string, unknown> | null {
    const tid = this.selectedTrainerId ?? this.trainerId ?? 0;
    if (tid <= 0 || !this.clients.length) {
      return null;
    }
    const first = this.clients[0];
    const dateVal = this.form.get('sessiondate')?.value;
    const timeVal = this.form.get('sessiontime')?.value;
    if (dateVal == null || dateVal === '' || timeVal == null || timeVal === '') {
      return null;
    }
    if (!this.scheduledEvent) {
      const day = moment(dateVal).startOf('day');
      const minAllowed = moment(this.editDateBounds.minDate).startOf('day');
      if (day.isBefore(minAllowed)) {
        return null;
      }
    }
    const startDate = moment(dateVal).startOf('day').format('YYYY-MM-DD');
    const startTime = `${moment(timeVal, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss']).format('HH:mm')}:00`;

    const type = this.isGroupSession() ? 2 : 1;
    const duration = this.getDurationForSelection();

    const memberIds = [...new Set(this.clients.map(c => c.id))];

    const isRecurring = this.isRecurringShift;
    let repeatInterval = 0;
    let repeatFrequency = 0;
    let repeatByDay = '';
    let recurrenceEndTypeApi = 1;
    let recurrenceOccurrences: number | null = 0;
    let recurrenceEndDateIso: string | null = null;

    if (!isRecurring) {
      repeatFrequency = 0;
      repeatInterval = 0;
      repeatByDay = '';
      recurrenceEndTypeApi = 1;
      recurrenceOccurrences = 0;
      recurrenceEndDateIso = null;
    } else {
      repeatInterval = this.parsedRepeatInterval();
      repeatFrequency = this.repeatFrequency === 'Daily' ? 4 : 5;
      repeatByDay = this.repeatFrequency === 'Weekly' ? repeatByDayToApiString(this.repeatByDay) : '';
      if (this.recurrenceEndType === 'after') {
        recurrenceEndTypeApi = 1;
        recurrenceOccurrences = this.parsedRecurrenceOccurrences();
        recurrenceEndDateIso = null;
      } else if (this.recurrenceEndType === 'on') {
        recurrenceEndTypeApi = 2;
        recurrenceOccurrences = null;
        if (this.recurrenceEndDate != null) {
          const d = moment(this.recurrenceEndDate);
          recurrenceEndDateIso = moment.utc([d.year(), d.month(), d.date()]).startOf('day').toISOString();
        }
      } else {
        recurrenceEndTypeApi = 1;
        recurrenceOccurrences = 365;
        recurrenceEndDateIso = null;
      }
    }

    return {
      scheduleId: this.coerceScheduleIdForPtBooking(first?.scheduleId),
      trainerId: tid,
      memberIds,
      startDate,
      startTime,
      type,
      duration,
      isFreeAssessment: this.isAssessmentSession(),
      isRecurring,
      repeatInterval,
      repeatFrequency,
      repeatByDay,
      isRepeatForever: false,
      recurrenceEndType: recurrenceEndTypeApi,
      recurrenceOccurrences,
      recurrenceEndDate: recurrenceEndDateIso
    };
  }

  openReviewModal(): void {
    if (!this.reviewBookingContent) {
      return;
    }
    this.blurActiveElement();
    this.reviewModalRef = this.modalService.open(this.reviewBookingContent, {
      size: 'md',
      centered: true,
      backdrop: true,
      backdropClass: 'su-rbd',
      windowClass: 'su-rw',
      modalDialogClass: 'su-rvd'
    });
    this.moveFocusIntoStackedModal(this.reviewModalRef, 'su-rw');
    this.reviewModalRef.result.catch(() => {}).finally(() => {
      this.reviewModalRef = null;
    });
  }

  closeReviewModal(): void {
    if (this.reviewModalRef) {
      this.reviewModalRef.close();
      this.reviewModalRef = null;
    }
  }

  onReviewBack(): void {
    this.closeReviewModal();
  }

  isReviewConfirmDisabled(): boolean {
    const p = this.reviewPreview;
    if (!p || !this.lastPtBookingBody) {
      return true;
    }
    if (!p.isValid) {
      return true;
    }
    if (p.attendees?.some(m => !m.hasSufficientBalance)) {
      return true;
    }
    if (this.confirmApiErrors.length > 0) {
      return true;
    }
    return false;
  }

  onReviewConfirm(): void {
    if (!this.lastPtBookingBody || this.isReviewConfirmDisabled()) {
      return;
    }
    this.confirmLoading = true;
    this.trainerService.confirmPtBooking(this.lastPtBookingBody).then(result => {
      this.confirmLoading = false;
      if (result.httpOk) {
        this.closeReviewModal();
        if (this.addModalRef) {
          this.addModalRef.close();
          this.addModalRef = null;
        }
        this.bookingSuccess.emit();
        this.toastService.showSuccess('Session(s) booked.');
        return;
      }
      const errs: string[] = [];
      if (result.transportError) {
        errs.push(result.transportError);
      }
      if (result.preview?.errors?.length) {
        errs.push(...result.preview.errors);
      }
      this.confirmApiErrors = errs.length ? errs : ['Confirm failed. Use Back to adjust, then preview again.'];
    }).catch(() => {
      this.confirmLoading = false;
      this.confirmApiErrors = ['Confirm request failed.'];
    });
  }

  memberNameForPreview(memberId: number): string {
    const fromClient = this.clients.find(c => c.id === memberId);
    if (fromClient?.name) {
      return fromClient.name;
    }
    const raw = this.clientsList.find(c => c.Id === memberId);
    return raw?.Person?.FullName || `Member ${memberId}`;
  }

  reviewHasIssues(): boolean {
    const p = this.reviewPreview;
    const errs = [
      ...(p?.errors ?? []),
      ...this.reviewExtraErrors,
      ...this.confirmApiErrors
    ];
    return errs.length > 0 || (p?.attendees?.some(m => !m.hasSufficientBalance) ?? false) || (!!p && !p.isValid);
  }

  allReviewErrorsList(): string[] {
    const p = this.reviewPreview;
    return [
      ...(p?.errors ?? []),
      ...this.reviewExtraErrors,
      ...this.confirmApiErrors
    ];
  }

  formatOccurrenceDate(d: Date | string | null): string {
    if (d == null || d === '') {
      return '—';
    }
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) {
      return '—';
    }
    return moment(dt).format('ddd, MMM D, YYYY');
  }

  formatOccurrenceTimeRange(start: string | null, end: string | null): string {
    const fmt = (t: string | null) => {
      if (!t) {
        return '';
      }
      const m = moment(t, ['HH:mm:ss', 'H:mm:ss', moment.ISO_8601], true);
      return m.isValid() ? m.format('h:mm A') : t;
    };
    const a = fmt(start);
    const b = fmt(end);
    if (a && b) {
      return `${a} – ${b}`;
    }
    return a || b || '—';
  }

  onSessionTypeChipClickWhenEditing(): void {
    if (!this.scheduledEvent) {
      return;
    }
    this.toastService.showInfo(
      "Session type can't be changed when editing — credits are tied to the original product."
    );
  }

  hasSessionDate() {
    const v = this.form.get('sessiondate')?.value;
    if (v == null || v === '') {
      return false;
    }
    if (v instanceof Date) {
      return !Number.isNaN(v.getTime());
    }
    return true;
  }

  hasSessionTime() {
    const v = this.form.get('sessiontime')?.value;
    if (v == null || v === '') {
      return false;
    }
    if (v instanceof Date) {
      return !Number.isNaN(v.getTime());
    }
    return String(v).trim().length > 0;
  }

  isFormChanged(): boolean {
    if (this.scheduledEvent) {
      const se = this.scheduledEvent;
      const sessionDate = this.form.get('sessiondate')?.value;
      const sessionTime = moment(this.form.controls['sessiontime'].value, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss']).format('H:m');
      const curDay = moment(sessionDate).startOf('day').valueOf();
      const oldDay = moment(se.StartDate).startOf('day').valueOf();
      const oldTime = moment(se.StartTime, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss']).format('H:m');
      return curDay !== oldDay || sessionTime !== oldTime;
    }
    return !!this.clients.length;
  }

  isFormValid(): boolean {
    return this.isPreviewReady();
  }

  /**
   * Preview button: add flow uses explicit checks (datepicker/flatpickr can lag `form.valid`); edit flow
   * requires a valid form and a changed date/time.
   */
  private isPreviewReady(): boolean {
    const dateVal = this.form.get('sessiondate')?.value;

    if (dateVal != null && dateVal !== '') {
      const m = moment(dateVal).startOf('day');
      const minD = moment(this.sessionStartDateMinAsDate()).startOf('day');
      const maxD = moment(this.editDateBounds.maxDate).startOf('day');
      if (m.isBefore(minD) || m.isAfter(maxD)) {
        return false;
      }
    }

    if (this.scheduledEvent) {
      return this.form.valid && this.isFormChanged();
    }

    const tid = this.selectedTrainerId ?? this.trainerId ?? 0;
    if (tid <= 0 || !this.clients.length) {
      return false;
    }
    if (this.isGroupSession() && this.clients.length < AddSessionComponent.MIN_GROUP_MEMBERS) {
      return false;
    }
    if (!this.hasSessionDate() || !this.hasSessionTime() || !this.isFormChanged()) {
      return false;
    }
    return true;
  }

  open(scheduledEvent: any | null = null, allScheduled?: Array<any>) {
    const se = scheduledEvent ?? null;
    if (se) {
      const startMs = AppSettings.parseSessionStartMs(se);
      const now = Date.now();
      if (startMs != null && startMs <= now && !AppSettings.isPastSessionEditable(se)) {
        this.toastService.showDanger(
          'Past sessions can only be edited within 72 hours of the scheduled start time.'
        );
        return;
      }
    }
    // Always assign: `open()` for add must clear a previous edit session on the same instance.
    this.scheduledEvent = se;
    this.allScheduled = allScheduled;
    this.reviewPreview = null;
    this.reviewExtraErrors = [];
    this.confirmApiErrors = [];
    this.lastPtBookingBody = null;
    this.addSessionErrorBanner = null;
    this.closeReviewModal();

    // Reset must run on enabled controls; edit leaves `sessiontype` disabled and `reset()` is unreliable otherwise.
    this.form.get('sessiontype')?.enable({ emitEvent: false });
    this.form.get('sessiondate')?.enable({ emitEvent: false });
    this.form.get('sessiontime')?.enable({ emitEvent: false });
    this.form.get('sessiontype')?.reset('1', { emitEvent: false });
    this.form.get('sessiondate')?.reset(null, { emitEvent: false });
    this.form.get('sessiontime')?.reset('', { emitEvent: false });

    this.clientsList = [];
    this.customers = [];
    this.clients = [];
    this.membersForSelectModal = [];
    this.lastMemberSearchTermResolved = '';
    this.memberRosterEmptyFetchTrainerId = null;
    this.memberRosterEmptyFetchAt = 0;
    this.initScheduledEvent();
    // Add flow keeps all controls enabled; edit flow disables session type in initScheduledEvent.
    if (!this.scheduledEvent) {
      this.form.get('sessiontype')?.enable({ emitEvent: false });
      this.form.get('sessiondate')?.enable({ emitEvent: false });
      this.form.get('sessiontime')?.enable({ emitEvent: false });
    }
    this.resetRecurrence();
    if (this.scheduledEvent) {
      if (this.trainerId != null) {
        this.loadClientRowForEdit();
      }
    }
    this.setLoggedInTrainerOnly();
    this.form.updateValueAndValidity({ emitEvent: true });

    const modalRef = this.modalService.open(this.content, {
      windowClass: 'su-aw',
      size: 'md',
      backdrop: true
    });
    this.addModalRef = modalRef;
    modalRef.result
      .catch(() => {
        /* dismissed */
      })
      .finally(() => {
        // A new open() may have replaced `addModalRef` before this async finally runs — do not wipe active modal state.
        if (this.addModalRef !== modalRef) {
          return;
        }
        this.addModalRef = null;
        this.scheduledEvent = null;
        this.allScheduled = undefined;
        this.cdr.markForCheck();
      });
  }

  initScheduledEvent() {
    if (this.scheduledEvent) {
      let se = this.scheduledEvent;
      let sessionType = this.form.controls['sessiontype'];
      const isGroup = se.Type === BookingSessionType.GroupPT;
      // Non-standard durations (e.g. 90) highlight no chip; type/duration then come from the event.
      const isAssessment = se.IsFreeAssessment === true || se.isFreeAssessment === true;
      const match = this.sessionTypes.find(
        (o) => o.isGroup === isGroup && o.isAssessment === isAssessment && o.duration === se.Duration
      );
      sessionType.setValue(match?.code ?? null);
      sessionType.disable();

      let sessionDate = this.form.controls['sessiondate'];
      sessionDate.setValue(moment(se.StartDate).toDate());
      sessionDate.enable();

      let sessionTime = this.form.controls['sessiontime'];
      const tm = moment(se.StartTime, ['HH:mm:ss', 'H:mm:ss', 'h:mm A', moment.ISO_8601], true);
      sessionTime.setValue(tm.isValid() ? tm.format('h:mm A') : String(se.StartTime ?? ''));
      sessionTime.enable();
    }
  }

  selectedSessionType() {
    return this.form.controls['sessiontype'].value;
  }

  sessionTypes: OfferedSessionType[] = [];

  private sessionTypeOption(code: unknown) {
    return this.sessionTypes.find((o) => o.code === String(code));
  }

  private loadOfferedSessionTypes(): void {
    this.trainerService.getOfferedSessionTypes().then((types) => {
      this.sessionTypes = types;
      if (!this.scheduledEvent && types.length && !this.form?.get('sessiontype')?.value) {
        this.form.get('sessiontype')?.setValue(types[0].code);
      }
      this.initScheduledEvent();
      this.cdr.markForCheck();
    });
  }

  /** Selected chip, or (when editing a non-standard duration) the scheduled event's own type/duration. */
  private selectedSessionInfo(): OfferedSessionType {
    const option = this.sessionTypeOption(this.selectedSessionType());
    if (option) {
      return option;
    }
    if (this.scheduledEvent) {
      const isGroup = this.scheduledEvent.Type === BookingSessionType.GroupPT;
      const isAssessment = this.scheduledEvent.IsFreeAssessment === true || this.scheduledEvent.isFreeAssessment === true;
      const duration = this.scheduledEvent.Duration;
      return {
        isGroup,
        isAssessment,
        duration,
        label: isAssessment ? 'Fitness Assessment' : `${isGroup ? 'Group' : 'Private'} (${duration} min)`,
        code: offeredSessionTypeCode(isAssessment, isGroup, duration)
      };
    }
    return this.sessionTypes[0] ?? {
      isGroup: false,
      isAssessment: false,
      duration: 60,
      label: 'Private (60 min)',
      code: 'p-60'
    };
  }

  /** True when a Group session type is selected. */
  isGroupSession(): boolean {
    return this.selectedSessionInfo().isGroup;
  }

  isAssessmentSession(): boolean {
    return this.selectedSessionInfo().isAssessment;
  }

  /**
   * + control: show below client list only when adding sessions, private has at most one member,
   * group caps at MAX_GROUP_MEMBERS.
   */
  showAddClientButton(): boolean {
    if (this.scheduledEvent) {
      return false;
    }
    if (this.resolvePtSearchTrainerId() === null) {
      return false;
    }
    if (!this.isGroupSession() && this.clients.length >= 1) {
      return false;
    }
    if (this.isGroupSession() && this.clients.length >= AddSessionComponent.MAX_GROUP_MEMBERS) {
      return false;
    }
    return true;
  }

  /** Duration in minutes for selected session type. */
  getDurationForSelection(): number {
    return this.selectedSessionInfo().duration;
  }

  /**
   * Usable minutes for the chosen slot: floor(session count at this duration) × duration.
   * Flexible pool is divided by duration; Strict lines only count when Scale matches duration.
   */
  getBalanceForBooking(client: Client, duration: number, isGroup: boolean, isAssessment = false): number {
    const balances = client.CurrencyBalances;
    if (!balances?.length) {
      if (isAssessment) {
        return 0;
      }
      const legacy = isGroup ? client.GroupSessions : client.PTSessions;
      return legacy >= 0 ? legacy : 0;
    }
    const n = sessionCountForBookingSlot(client, duration, isGroup, isAssessment);
    if (duration > 0) {
      return Math.floor(n + 1e-9) * duration;
    }
    return 0;
  }

  /** Usable minutes for the currently selected session type on the form. */
  getBalanceForSelection(client: Client): number {
    return this.getBalanceForBooking(
      client,
      this.getDurationForSelection(),
      this.isGroupSession(),
      this.isAssessmentSession()
    );
  }

  /** Session count at current slot (same rules as balance lines / recurrence cap). */
  sessionsLeft(client: Client): string {
    const duration = this.getDurationForSelection();
    const isGroup = this.isGroupSession();
    return formatSessionCountForDisplay(
      sessionCountForBookingSlot(client, duration, isGroup, this.isAssessmentSession())
    );
  }

  /** Whether the client can book at least one session for the given duration / category. */
  hasEnoughBalanceForBooking(client: Client, duration: number, isGroup: boolean, isAssessment = false): boolean {
    return sessionCountForBookingSlot(client, duration, isGroup, isAssessment) >= 1 - 1e-9;
  }

  /** Whether the client can book at least one session of the selected duration (Flexible + Strict rules). */
  hasEnoughBalanceForSelection(client: Client): boolean {
    return this.hasEnoughBalanceForBooking(
      client,
      this.getDurationForSelection(),
      this.isGroupSession(),
      this.isAssessmentSession()
    );
  }

  getBalanceDisplayLines(client: Client): string[] {
    return getBalanceDisplayLinesForBooking(
      client,
      this.getDurationForSelection(),
      this.isGroupSession(),
      this.isAssessmentSession()
    );
  }

  /** Shared mapper used by Add/Edit selected-member UI cards. */
  private mapClientForSelection(client: Client, fallbackName = ''): Record<string, unknown> {
    return {
      id: client.Id,
      scheduleId: client.ScheduleId,
      avatar: client.Avatar?.UploadedUrl || '',
      name: client.Person?.FullName || fallbackName,
      sessionsLeft: this.sessionsLeft(client),
      disabled: !this.hasEnoughBalanceForSelection(client),
      state: client.State,
      balanceLines: this.getBalanceDisplayLines(client),
      isInactive: client.State !== 1
    };
  }

  getCustomers(): Array<any> {
    if (!this.clientsList?.length) {
      return [];
    }
    return this.clientsList.map(client => this.mapClientForSelection(client));
  }

  /**
   * Member cards always read `this.clients` in the template so `*ngFor` identity is stable.
   * (Previously the template called `getClients()`, which allocated a new array/objects every
   * change-detection cycle for private edit → flickering.)
   */
  getClients() {
    if (this.scheduledEvent) {
      if (this.isGroupPtEditSlot() && this.clients?.length) {
        return this.clients;
      }
      if (this.clients?.length) {
        return this.clients;
      }
      const se = this.scheduledEvent;
      const memberId = se.Member?.Id ?? se.MemberId;
      const client = this.clientsList?.find(c => c.Id === memberId);
      if (client) {
        return [this.mapClientForSelection(client)];
      }
      return [];
    }
    return this.clients;
  }

  trackMemberCard(_index: number, item: { id?: number }): number {
    return item?.id ?? _index;
  }

  /** Whether this member is already in the selected clients list (by id). */
  isMemberSelected(member: any): boolean {
    return this.clients.some(c => c.id === member.id);
  }

  /** Select/deselect: private allows picking another member (replaces); group caps at 4. */
  addAllowed(member: any): boolean {
    if (this.isMemberSelected(member)) {
      return true;
    }
    if (member?.disabled) {
      return false;
    }
    if (this.isGroupSession() && this.clients.length >= AddSessionComponent.MAX_GROUP_MEMBERS) {
      return false;
    }
    return true;
  }

  addClient(member: any) {
    if (this.isMemberSelected(member)) {
      return;
    }
    if (member.state !== 1) {
      alert('Member is not onboarded. Make sure they go through onboarding before starting their sessions.');
    }
    if (!this.isGroupSession()) {
      this.clients = [member];
    } else {
      this.clients = [...this.clients, member];
    }
    this.refreshSelectedClientsDisplay();
    if (this.clientsList?.length) {
      this.customers = this.getCustomers();
    }
    this.resetRecurrence();
    this.cdr.markForCheck();
  }

  removeClient(client: any) {
    if (
      this.isGroupSession() &&
      this.clients.length <= AddSessionComponent.MIN_GROUP_MEMBERS &&
      this.clients.some(c => c.id === client.id)
    ) {
      return;
    }
    const i = this.clients.findIndex(c => c.id === client.id);
    if (i >= 0) {
      this.clients = [...this.clients.slice(0, i), ...this.clients.slice(i + 1)];
      this.rebuildMembersForSelectModal();
      this.resetRecurrence();
      this.cdr.markForCheck();
    }
  }

  /** Toggle member in Select members modal: add if not selected, remove if selected. */
  toggleMemberSelection(member: any) {
    if (this.isMemberSelected(member)) {
      if (this.isGroupSession() && this.clients.length <= AddSessionComponent.MIN_GROUP_MEMBERS) {
        return;
      }
      this.removeClient(member);
      return;
    }
    if (!this.addAllowed(member)) {
      return;
    }
    this.addClient(member);
  }

  removeAllowed(_client: any): boolean {
    return !this.scheduledEvent;
  }

  isTimeSet() {
    return !!this.form.controls['sessiondate'].value && !!this.form.controls['sessiontime'].value;
  }

  untilTime() {
    if (!this.form.controls['sessiontime'].value) return '';
    let duration = this.getDurationForSelection();
    let until = `- ${moment(this.form.controls['sessiontime'].value, ['h:mm A', 'h:m a', 'H:m', 'HH:mm:ss']).add(duration, 'minutes').format('LT')}`;
    return until;
  }

  isEditing(): boolean {
    return !!this.scheduledEvent;
  }

  onCancelSession(modal: any) {
    modal.close('Cancel Session');
    this.cancelSession.emit({
      bookingId: this.scheduledEvent.Id,
      startDateTime: moment(Date.parse(this.scheduledEvent.StartDate)).format('YYYY-MM-DD') + 'T'
        + this.scheduledEvent.StartTime
    });
  }

  onCancelAllSessions(modal: any) {
    if (!this.allScheduled || !this.allScheduled.length) return;
    modal.close('Cancel All Sessions');
    const se = this.scheduledEvent;
    if (se?.Type === BookingSessionType.GroupPT) {
      this.cancelSession.emit({
        cancelEntireGroupSession: true,
        bookingId: se.Id ?? se.id,
        startDateTime: moment(Date.parse(se.StartDate)).format('YYYY-MM-DD') + 'T' + se.StartTime
      });
      return;
    }
    const allSessions: any[] = [];
    this.allScheduled.forEach(session => {
      allSessions.push({
        bookingId: session.Id,
        startDateTime: moment(Date.parse(session.StartDate)).format('YYYY-MM-DD') + 'T'
          + session.StartTime
      });
    });
    this.cancelSession.emit(allSessions);
  }

  yearsFromToday(year: number = 0) {
    let today = new Date();
    var yearFromDate = today.getFullYear();
    var monthFromYear = today.getMonth();
    var dayFromYear = today.getDate();
    var newDate = new Date(yearFromDate + year, monthFromYear, dayFromYear);
    return newDate;
  }

  monthsFromToday(months: number = 0) {
    let today = new Date();
    var yearFromDate = today.getFullYear();
    var monthFromYear = today.getMonth();
    var dayFromYear = 1; // defaulting to 1st of the month in case of non-existing date
    var newDate = new Date(yearFromDate, monthFromYear + months, dayFromYear);
    return newDate;
  }

  /** Reset inline recurrence (df-frontdesk defaults when clients/type change). */
  resetRecurrence() {
    this.isRecurringShift = false;
    this.repeatIntervalStr = '1';
    this.repeatFrequency = 'Weekly';
    this.repeatByDay = [false, false, false, false, false, false, false];
    this.recurrenceEndType = 'after';
    this.recurrenceOccurrencesStr = '1';
    this.recurrenceEndDate = null;
  }

  futureSessions(): number {
    return this.allScheduled ? this.allScheduled.length : 1;
  }

  /** True when editing a group PT row (multiple members share the slot). */
  isGroupPtEditSlot(): boolean {
    return this.scheduledEvent?.Type === BookingSessionType.GroupPT;
  }

}
